
import * as React from 'react';
import { FileRecord, FileReferenceType, NonVideoFileExtensions } from '../../amorg-common';
import { deleteReferenceFile, getReferenceFiles, uploadReferenceFile } from '../../api/filesClientApi';
import { FileUploadButton } from '../formField/FileUploadButton';
import { Flex } from './FlexHelpers';
import { Card } from '../Card';
import { getFileExtensionIconProps } from '../FileExtensionIcon';
import { FileLinkWithTokenRefresh } from '../FileLinkWithTokenRefresh';
import { Small } from '../typography/Body';
import { LoadingPageOverlay } from '../LoadingPageOverlay';
import { MessageCard } from '../Message';
import { toDateTimeString } from '../../utils/date';
import { ListItem } from '../List/ListItem';
import { Button } from '../button/Button';

type ReferenceFilesPanelProps = {
    objectId:string,
    referenceType:FileReferenceType,
    /** Use false inside a modal for local loading feedback. Deletion confirmation is always inline. */
    modalEditing:boolean,
    hide?:boolean,
    onGetFilesCount?: (count:number) => void
}


export function ReferenceFilesPanel(props:ReferenceFilesPanelProps):JSX.Element
{
    const [loading, setLoading] = React.useState<boolean>(false);
    const [files, setFiles] = React.useState<FileRecord[]>([]);
    const [error, setError] = React.useState<string>('');
    
    React.useEffect(() => {
        getReferenceFiles({ type: props.referenceType, id: props.objectId })
        .then(fetchedFiles => {
                setFiles(fetchedFiles);
                props.onGetFilesCount?.(fetchedFiles.length);
                setLoading(false);
            });
    }, [props.objectId, props.referenceType]);

    
    async function handleFileUpload(file:File):Promise<void>
    {
        setLoading(true);
        const result = await uploadReferenceFile({ type: props.referenceType, id: props.objectId }, file);
        if(!result.isSuccess)
        {
            if(result.errorMessage === 'uploadReferenceFile_empty_file')
                setError('Filen er tom. Vælg en gyldig fil.');

            else if(result.errorMessage === 'uploadReferenceFile_file_too_big')
                setError('Filen er for stor. Vælg en fil på maks. 50 MB.');

            else if(result.errorMessage === 'uploadReferenceFile_invalid_public_file_name')
                setError('Ugyldigt filnavn. Vælg en gyldig fil.');

            else if(result.errorMessage === 'uploadReferenceFile_public_file_name_in_use')
                setError('Filnavnet er allerede i brug.');

            else if(result.errorMessage === 'uploadReferenceFile_invalid_file_extension')
                setError('Filtypen er ikke understøttet. Vælg en gyldig fil.');

            else {
                setError('Der opstod en fejl. Prøv igen.');
            }

            setLoading(false);
        }
        else {
            setError('');
            const updatedFiles = await getReferenceFiles({ type: props.referenceType, id: props.objectId });
            setFiles(updatedFiles);
            setLoading(false);
            props.onGetFilesCount?.(updatedFiles.length);
        }
    }

    async function handleFileDelete(privateFileName:string):Promise<boolean>
    {
        setLoading(true);
        const result = await deleteReferenceFile(privateFileName);
        if(!result.isSuccess)
        {
            setError('Der opstod en fejl ved sletning af filen. Prøv igen.');
            setLoading(false);
            return false;
        }
        else{
            setError('');
            const updatedFiles = await getReferenceFiles({ type: props.referenceType, id: props.objectId });
            setFiles(updatedFiles);
            setLoading(false);
            props.onGetFilesCount?.(updatedFiles.length);
        }
        return true;
    }

    const editingPanelProps:ReferenceFilesEditingPanelProps = {
        files,
        error,
        loading,
        hide: props.hide,
        onFileUpload: handleFileUpload,
        onFileDelete: handleFileDelete
    };

    return props.modalEditing
        ? <ModalEditingReferenceFilesPanel {...editingPanelProps} />
        : <InlineEditingReferenceFilesPanel {...editingPanelProps} />;
}

type ReferenceFilesEditingPanelProps = {
    files:FileRecord[],
    error:string,
    loading:boolean,
    hide?:boolean,
    onFileUpload:(file:File) => Promise<void>,
    onFileDelete:(privateFileName:string) => Promise<boolean>
}

function InlineEditingReferenceFilesPanel(props:ReferenceFilesEditingPanelProps):JSX.Element
{
    if(props.hide)
        return <></>;

    return (
        <Flex flexDirection="column" justifyContent="stretch" grow="1" gap={8} style={{ minWidth:0 }}>
            {props.loading && <Small role="status">Indlæser...</Small>}
            <ReferenceFilesList
                files={props.files}
                error={props.error}
                loading={props.loading}
                onDeleteConfirmation={props.onFileDelete}
            />
            <Flex justifyContent="flex-end">
                <FileUploadButton
                    size="32px"
                    inputName="uploaded_file"
                    fileTypes={[...NonVideoFileExtensions]}
                    disabled={props.loading}
                    onClickUpload={props.onFileUpload}
                    text="Upload fil"
                />
            </Flex>
        </Flex>
    );
}

function ModalEditingReferenceFilesPanel(props:ReferenceFilesEditingPanelProps):JSX.Element
{
    if(props.hide)
        return <></>;

    return (
        <Flex flexDirection="column" justifyContent="stretch" grow="1" gap={8} style={{ minWidth:0 }}>
            <LoadingPageOverlay loading={props.loading} />
            <ReferenceFilesList
                files={props.files}
                error={props.error}
                loading={props.loading}
                onDeleteConfirmation={props.onFileDelete}
            />
            <Flex justifyContent="flex-end">
                <FileUploadButton
                    size="32px"
                    inputName="uploaded_file"
                    fileTypes={[...NonVideoFileExtensions]}
                    onClickUpload={props.onFileUpload}
                    text="Upload fil"
                />
            </Flex>
        </Flex>
    );
}

type ReferenceFilesListProps = {
    files:FileRecord[],
    error:string,
    loading:boolean,
    onDeleteConfirmation:(privateFileName:string) => Promise<boolean>
}

function ReferenceFilesList(props:ReferenceFilesListProps):JSX.Element
{
    const [deleteConfirmationPrivateFileName, setDeleteConfirmationPrivateFileName] = React.useState<string | null>(null);

    async function handleDeleteConfirmation(privateFileName:string):Promise<void>
    {
        if(await props.onDeleteConfirmation(privateFileName))
            setDeleteConfirmationPrivateFileName(null);
    }

    return (
        <Flex flexDirection="column" grow="1" gap={4}>
            {props.files.map(file => {
                const fileIconProps = getFileExtensionIconProps(file.publicFileName);

                return (
                    <React.Fragment key={file.privateFileName}>
                        <ListItem
                            border
                            padding="6px"
                            leadingIcon={fileIconProps.name}
                            leadingIconColor={fileIconProps.color}
                            leadingIconBorder
                            primaryContent={
                                <FileLinkWithTokenRefresh
                                    publicFileName={file.publicFileName}
                                    privateFileName={file.privateFileName}
                                    style={{
                                        fontSize: '14px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                />
                            }
                            secondary={{
                                content: `${(file.sizeBytes / 1024).toFixed(2)} KB  ${toDateTimeString(file.created)}`
                            }}
                            buttons={[
                                {
                                    type:'download',
                                    href: '/files/' + file.privateFileName,
                                    fileName: file.publicFileName,
                                },
                                {
                                    type:'button',
                                    icon:'interface_actions_delete',
                                    destructive: true,
                                    onClick: () => {
                                        if(!props.loading)
                                            setDeleteConfirmationPrivateFileName(file.privateFileName);
                                    }
                                }
                            ]}
                        />
                        {deleteConfirmationPrivateFileName === file.privateFileName &&
                            <Card variant="none" padding="8px">
                                <Flex alignItems="center" gap={8}>
                                    <Small>Er du sikker på, at du vil slette denne fil?</Small>
                                    <Flex justifyContent="flex-end" gap={8} flexGrow="1">
                                        <Button
                                            variant="text"
                                            size="32px"
                                            text="Annuller"
                                            disabled={props.loading}
                                            onClick={() => setDeleteConfirmationPrivateFileName(null)}
                                        />
                                        <Button
                                            variant="destructive-text"
                                            size="32px"
                                            icon="interface_actions_delete"
                                            text="Slet"
                                            disabled={props.loading}
                                            onClick={() => handleDeleteConfirmation(file.privateFileName)}
                                        />
                                    </Flex>
                                </Flex>
                            </Card>
                        }
                    </React.Fragment>
                );
            })}
            {props.files.length === 0 && <Small>Ingen filer vedhæftet</Small>}
            {props.error &&
                <Flex flexDirection="row" justifyContent="center" alignItems="center">
                    <MessageCard variant="error" grow={1}>{props.error}</MessageCard>
                </Flex>
            }
        </Flex>
    );
}